import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register service worker for PWA installability + keep it fresh.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        reg.update().catch(() => {});
        setInterval(() => reg.update().catch(() => {}), 30 * 60 * 1000);
      })
      .catch(() => {});
  });
}

// Self-update: check whether a new build was deployed; if so, clear caches and reload.
let updateCheckedAt = 0;
async function checkForNewVersion() {
  const now = Date.now();
  if (now - updateCheckedAt < 60_000) return;
  updateCheckedAt = now;
  try {
    const currentScript = Array.from(document.scripts)
      .map((s) => s.src)
      .find((s) => s.includes("/static/js/main."));
    if (!currentScript) return; // dev mode — no hashed bundle
    const res = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store" });
    const html = await res.text();
    const m = html.match(/static\/js\/main\.[^"']+\.js/);
    if (!m || currentScript.includes(m[0])) return;
    if (window.caches) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    const reg = await navigator.serviceWorker?.getRegistration?.();
    await reg?.update?.().catch(() => {});
    window.location.reload();
  } catch {
    // offline or fetch failed — ignore
  }
}
window.addEventListener("load", () => setTimeout(checkForNewVersion, 3000));
setInterval(checkForNewVersion, 3 * 60 * 1000);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") checkForNewVersion();
});
